import axios from 'axios';
import toast from 'react-hot-toast';
import tokenStorage from '../utils/tokenStorage';

const TIMEOUT_TOAST_ID = 'timeout-error-toast';
const GENERIC_ERROR_TOAST_ID = 'generic-api-error-toast';

// Token refresh lock to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

// Track consecutive refresh failures to prevent retry loops.
// After MAX_REFRESH_FAILURES consecutive failures the client stops
// hammering the server and forces a logout instead.
const MAX_REFRESH_FAILURES = 3;
let consecutiveRefreshFailures = 0;

// Secure API URL configuration
const API_URL = (() => {
  // Always use environment variable for API URL
  // This prevents hardcoding infrastructure details in the codebase
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Development fallback only
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5000/api';
  }
  
  // Production requires explicit API URL via environment variable
  throw new Error('API URL configuration error - REACT_APP_API_URL environment variable must be set in production');
})();

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 seconds - reduced from 30s to prevent hanging requests
  withCredentials: true, // Include cookies in requests
});

// Force-logout helper — clears tokens, calls logout endpoint, and redirects
const forceLogout = async () => {
  tokenStorage.clearAccessToken();
  const userData = localStorage.getItem('userData');
  let redirectPath = '/login';

  if (userData) {
    try {
      const user = JSON.parse(userData);
      if (user.role === 'admin') redirectPath = '/admin/login';
      else if (user.role === 'technician') redirectPath = '/technician/login';
    } catch (_) { /* corrupted data, default to /login */ }
  }

  localStorage.removeItem('accessToken');
  localStorage.removeItem('userData');

  try {
    await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
  } catch (_) { /* best-effort */ }

  if (window.location.pathname !== redirectPath && window.location.pathname !== '/') {
    window.location.href = redirectPath;
  }
};

// Helper function to refresh token
const refreshAccessToken = async () => {
  // If we've already failed too many times, stop retrying and force logout
  if (consecutiveRefreshFailures >= MAX_REFRESH_FAILURES) {
    const err = new Error('Max token refresh attempts exceeded — logging out');
    err.response = { status: 401, data: { message: 'expired' } };
    await forceLogout();
    throw err;
  }

  if (isRefreshing) {
    // If already refreshing, wait for the existing refresh to complete
    return new Promise((resolve) => {
      refreshSubscribers.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    // Add timeout to prevent hanging on slow networks
    const response = await axios.post(`${API_URL}/auth/refresh-token`, {}, {
      withCredentials: true,
      timeout: 10000, // 10 seconds timeout for token refresh
    });

    const { accessToken } = response.data.data;
    tokenStorage.setAccessToken(accessToken);

    // Reset failure counter on success
    consecutiveRefreshFailures = 0;

    // Notify all waiting subscribers
    refreshSubscribers.forEach((resolve) => resolve(accessToken));
    refreshSubscribers = [];

    return accessToken;
  } catch (error) {
    // Increment failure counter
    consecutiveRefreshFailures += 1;

    // Notify all waiting subscribers of failure
    refreshSubscribers.forEach((resolve) => resolve(null));
    refreshSubscribers = [];
    throw error;
  } finally {
    isRefreshing = false;
  }
};

// Helper function to check if current page is public (doesn't require auth)
const isPublicPage = () => {
  const currentPath = window.location.pathname;
  const publicPaths = [
    '/',
    '/login',
    '/register',
    '/admin/login',
    '/technician/login',
    '/technician/apply',
    '/contact',
    '/about',
    '/terms',
    '/privacy',
    '/razorpay-terms',
    '/services',
    '/services/search',
    '/help',
  ];
  return publicPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'));
};

// Helper function to check if user appears to be logged out
const isUserLoggedOut = () => {
  // Check if user has valid token and user data
  const token = tokenStorage.getAccessToken();
  const userData = localStorage.getItem('userData');
  
  // User appears logged out if no token or no user data
  return !token || !userData;
};

// Request interceptor with proactive token refresh
api.interceptors.request.use(
  async (config) => {
    // Skip refresh for public auth endpoints (login, register, OTP, etc.)
    // These endpoints don't require authentication and shouldn't trigger token refresh
    const publicAuthEndpoints = [
      '/auth/refresh-token',
      '/auth/login',
      '/auth/logout',
      '/auth/send-login-otp',
      '/auth/verify-login-otp',
      '/auth/send-registration-otp',
      '/auth/register',
      '/auth/send-technician-login-otp',
      '/auth/verify-technician-login-otp',
      '/auth/admin-login',
      '/auth/verify-phone-otp',
      '/auth/resend-phone-otp',
    ];
    
    if (publicAuthEndpoints.some(endpoint => config.url?.includes(endpoint))) {
      return config;
    }

    // Check if token is expiring soon or already expired
    const token = tokenStorage.getAccessToken();
    const isExpiringSoon = tokenStorage.isTokenExpiringSoon();
    const expiry = tokenStorage.getTokenExpiry();
    const isExpired = expiry && Date.now() >= expiry;
    
    // Skip proactive refresh if:
    // 1. User is on public page AND appears logged out
    // This prevents unnecessary refresh attempts for logged-out users
    // Don't check expiration state - if user appears logged out, skip refresh
    const shouldSkipRefresh = isPublicPage() && isUserLoggedOut();
    
    // If token is expired or expiring soon, try to refresh it proactively
    // But skip if user appears logged out on public pages (they'll login fresh)
    if ((isExpired || isExpiringSoon) && !config._skipTokenRefresh && !shouldSkipRefresh) {
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`;
          return config;
        }
      } catch (refreshError) {
        // Only log error if it's not a network issue
        // Don't logout here - let the response interceptor handle 401 errors
        // This prevents unnecessary logouts on temporary network issues
        if (refreshError.response?.status === 401) {
          // Token is truly invalid - response interceptor will handle logout
          
        } else {
          // Network error or temporary issue - let request proceed, will retry on 401
          // Don't show error toast for refresh failures - response interceptor will handle 401s
        }
      }
    }
    
    // Add auth token to requests from secure storage
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Return data directly
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    
    

    // Handle 401 errors (token expired)
    // Skip token refresh if we're already on a login page or home page to prevent redirect loops
    const currentPath = window.location.pathname;
    const isOnLoginPage = currentPath.includes('/login') || currentPath.includes('/admin/login') || currentPath.includes('/technician/login');
    const isOnHomePage = currentPath === '/';
    
    if (error.response?.status === 401 && !originalRequest._retry && !isOnLoginPage && !isOnHomePage) {
      originalRequest._retry = true;

      try {
        // Try to refresh token using the shared refresh function
        const accessToken = await refreshAccessToken();

        if (accessToken) {
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          originalRequest._skipTokenRefresh = true; // Skip proactive refresh on retry
          const retryResponse = await api(originalRequest);
          
          // Ensure credentials are included for all subsequent requests
          originalRequest.withCredentials = true;
          
          return retryResponse;
        } else {
          throw new Error('Token refresh returned no token');
        }
      } catch (refreshError) {
        // Only logout if refresh token is truly invalid/expired
        // Don't logout on network errors or temporary issues
        const isTokenExpired = refreshError.response?.status === 401 && 
                               (refreshError.response?.data?.message?.includes('expired') || 
                                refreshError.response?.data?.message?.includes('Invalid'));
        
        // If it's a token expiration/invalid error, logout user
        if (isTokenExpired) {
          await forceLogout();
        } else {
          // Network error or temporary issue - don't logout, just reject the request
          // The user can retry the action
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    let errorMessage = error.response?.data?.message || error.message || 'Something went wrong';
    
    // Handle timeout errors specifically
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      errorMessage = 'Request timed out. Please check your internet connection and try again.';
    } else if (error.message?.includes('Network Error')) {
      errorMessage = 'Network error. Please check your internet connection.';
    }
    
    // Don't show toast for certain errors (handle them in components)
    // Also prevent duplicate timeout toasts - only show one at a time
    // Skip showing error toast for:
    // 1. Token refresh failures on public pages (users can login fresh)
    // 2. Network errors on public pages when user appears logged out (non-critical)
    const isRefreshError = originalRequest.url?.includes('/auth/refresh-token');
    const isNetworkError = error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('Network Error');
    const shouldSuppressError = isPublicPage() && (
      isRefreshError || 
      (isNetworkError && isUserLoggedOut())
    );
    
    if (!originalRequest.skipErrorToast && !shouldSuppressError) {
      // Prevent duplicate timeout error toasts
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        // Check if a timeout toast was already shown recently (within last 5 seconds)
        let lastTimeoutToast = null;
        try {
          lastTimeoutToast = sessionStorage.getItem('lastTimeoutToast');
        } catch (storageError) {
          // Session storage unavailable, timeout toast throttling disabled
        }
        const now = Date.now();
        if (!lastTimeoutToast || (now - parseInt(lastTimeoutToast, 10)) > 5000) {
          toast.error(errorMessage, { id: TIMEOUT_TOAST_ID });
          try {
            sessionStorage.setItem('lastTimeoutToast', now.toString());
          } catch (storageError) {
            // Failed to persist timeout toast timestamp
          }
        }
      } else {
        toast.error(errorMessage, { id: GENERIC_ERROR_TOAST_ID });
      }
    }

    // Preserve the original error structure so components can access both
    // the error data and the config (for skipErrorToast checks)
    const rejectedError = error.response?.data || error;
    // Attach config to the rejected error so it's available in catch blocks
    if (rejectedError && typeof rejectedError === 'object' && !rejectedError.config) {
      rejectedError.config = originalRequest;
    }
    return Promise.reject(rejectedError);
  }
);

export default api;


